import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import cookie from "react-cookies";
import { getJob, startJobApplication } from "../../services/jobsApi";
import {
  DEPARTMENT_LABELS,
  EmploymentPill,
  JobPill,
  JobText,
  JobsShell,
  formatClosing,
} from "./jobsShared";

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getJob(slug)
      .then((res) => {
        if (!cancelled) setJob(res.job);
      })
      .catch((e) => {
        if (!cancelled) setError(e.status === 404 ? "This position was not found." : e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onApply = async () => {
    if (!cookie.load("candidateKey")) {
      navigate(`/login?next=${encodeURIComponent(`/jobs/${slug}`)}`);
      return;
    }
    setApplying(true);
    setError("");
    try {
      const res = await startJobApplication(slug);
      navigate(`/me/applications/${res.application.id}`);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate(`/login?next=${encodeURIComponent(`/jobs/${slug}`)}`);
        return;
      }
      setError(e.message || "Could not start your application");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <JobsShell>
        <section className="careers-section">
          <p className="jobs-note">Loading position...</p>
        </section>
      </JobsShell>
    );
  }

  if (!job) {
    return (
      <JobsShell>
        <section className="careers-section">
          <div className="jobs-empty">
            <h2>{error || "Position not found"}</h2>
            <Link to="/jobs" className="careers-btn careers-btn--secondary">
              See open positions
            </Link>
          </div>
        </section>
      </JobsShell>
    );
  }

  const closing = formatClosing(job.closes_at);

  return (
    <JobsShell>
      <Helmet>
        <title>{`${job.title} - QikTruck Careers`}</title>
        <meta name="description" content={job.summary || `Apply for ${job.title} at QikTruck.`} />
        <link rel="canonical" href={`https://careers.qiktruck.co.za/jobs/${job.slug}`} />
      </Helmet>

      <section className="careers-section job-detail">
        <Link to="/jobs" className="job-detail__back">
          All open positions
        </Link>
        <h1 className="careers-h1">{job.title}</h1>
        <div className="job-card__pills">
          <EmploymentPill type={job.employment_type} />
          <JobPill tone="slate">{DEPARTMENT_LABELS[job.department] || job.department}</JobPill>
          {job.is_remote ? <JobPill tone="green">Remote</JobPill> : null}
        </div>

        <dl className="job-detail__facts">
          {job.pay_summary ? (
            <div>
              <dt>Pay</dt>
              <dd>{job.pay_summary}</dd>
            </div>
          ) : null}
          {job.location ? (
            <div>
              <dt>Where</dt>
              <dd>{job.location}</dd>
            </div>
          ) : null}
          {closing ? (
            <div>
              <dt>Apply by</dt>
              <dd>{closing}</dd>
            </div>
          ) : null}
        </dl>

        {job.summary ? <p className="careers-lead">{job.summary}</p> : null}

        {job.description ? (
          <div className="job-detail__block">
            <h2>About the role</h2>
            <JobText text={job.description} />
          </div>
        ) : null}

        {job.requirements ? (
          <div className="job-detail__block">
            <h2>What we are looking for</h2>
            <JobText text={job.requirements} />
          </div>
        ) : null}

        <div className="job-detail__block">
          <h2>How to apply</h2>
          <p className="job-text__p">
            Sign in with your email, add a short note, and
            {job.video_required ? " upload" : " optionally upload"} a vertical intro video of
            about 30 to 60 seconds. We watch every one.
          </p>
          {job.video_prompt ? (
            <div className="job-detail__prompt">
              <strong>What to say in your video</strong>
              <p>{job.video_prompt}</p>
            </div>
          ) : null}
        </div>

        {error ? <p className="jobs-note jobs-note--error">{error}</p> : null}

        {job.is_open ? (
          <div className="job-detail__actions">
            <button
              type="button"
              className="careers-btn careers-btn--primary"
              disabled={applying}
              onClick={onApply}
            >
              {applying ? "Starting..." : "Apply now"}
            </button>
            <Link to="/me/applications" className="careers-btn careers-btn--ghost">
              My applications
            </Link>
          </div>
        ) : (
          <p className="jobs-note">This position is not taking applications right now.</p>
        )}
      </section>
    </JobsShell>
  );
}
